import { autoinject, computedFrom, observable } from 'aurelia-framework';
import { Subscription, EventAggregator } from 'aurelia-event-aggregator';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/api/api';
import { validate } from 'app/shared/elements/ess-rule';
import { getReference } from 'app/shared/models/essette-types';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { CaseService } from 'app/cm/services/case-service';
import { CaseChangeEvent } from 'app/cm/lib/events';
import { CaseStatusHistoryGrid } from 'app/cm/components/summary/case-status-history-grid';
import * as CM from 'app/cm/models/essette-api';
import { CaseCheckoutHistoryGrid } from 'app/cm/components/summary/case-checkout-history-grid';


@autoinject()
export class CaseStatusPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    private caseId: string | null = null;
    protected case: CM.Case | null = null;

    @observable
    protected selectedStatus: CM.CaseStatus | null = null;
    protected selectedStatusReason: CM.CaseStatusReason | null = null;
    protected statuses: ReadonlyArray<CM.CaseStatusWithReasonModel> = new Array();
    protected reasons: ReadonlyArray<CM.CaseStatusReason> | null = null;

    protected readonly: boolean = false;
    protected statusIsReadonly: boolean = false;
    protected canEditClosedCase: boolean = false;
    protected isCMOfflineEnabled: boolean = false;

    @computedFrom('selectedStatus', 'selectedStatusReason', 'case')
    protected get hasStatusChanged(): boolean {
        if(this.selectedStatus?.id === this.case?.status?.id && this.selectedStatusReason?.id === this.case?.status?.reason?.id) {
            return false;
        }
        return true;
    }

    private subscriptions: Array<Subscription> = [];

    protected validate = validate;


    ////****************** Element References ***************************************////
    private $statusHistory!: CaseStatusHistoryGrid;
    private $checkoutHistory!: CaseCheckoutHistoryGrid;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly context: CasePageContext,
        private readonly service: CaseService,
        private readonly events: EventAggregator,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected async canActivate(params: { caseId: string }): Promise<boolean> {
        if(!params.caseId) { throw new Error('Missing or invalid caseId parameter'); }

        return true;
    }

    protected async activate(params: { caseId: string }): Promise<void> {
        if(this.caseId !== params.caseId) { this.caseId = params.caseId; }
        this.case = this.context.case;

        this.checkPermissions();

        this.loadLookups();

        this.subscriptions.push(
            this.events.subscribe(CaseChangeEvent, (e: CaseChangeEvent) => this.handleCaseChangeEvent(e))
        );
    }

    protected deactivate(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }


    ////****************** Events ***************************************************////
    protected async handleCaseChangeEvent(e: CaseChangeEvent) {
        if(e.case && e.case.id === this.caseId) {
            this.checkPermissions();
            this.reload();
        };
    }

    protected selectedStatusChanged(newValue: CM.CaseStatus, oldValue: CM.CaseStatus) {
        if(newValue && newValue.id && this.statuses) {
            const foundStatus = this.statuses.find(s => s.id === newValue.id);
            this.reasons = null;
            if(foundStatus && foundStatus.reasons) {
                this.reasons = foundStatus.reasons;
                this.selectedStatusReason = null;
            }
        }
        else {
            this.reasons = null;
        }
        this.selectedStatusReason = null;
    }


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.canEditClosedCase = this.accountContext.hasPermission('CM.Case.Duty.EditClosedCase');
        this.isCMOfflineEnabled = this.env.config.integrations.includes('cm-offline');

        if(this.context && this.context.case) {
            this.readonly = this.context.caseIsReadOnly(this.canEditClosedCase);
            this.statusIsReadonly = (this.readonly && !this.canEditClosedCase);
        }
    }

    public async reload(): Promise<void> {
        this.case = this.context.case;
        await this.loadLookups();
        if(this.$statusHistory) await this.$statusHistory.reload();
        if(this.$checkoutHistory) await this.$checkoutHistory.reload();
    }

    protected reset(): void {
        this.selectedStatus = null;
        this.selectedStatusReason = null;
    }

    private constructSaveObject(): CM.Case {
        if(!this.case?.id || !this.selectedStatus?.id) { return {}; }

        const saveCase: CM.Case = {
            id: this.case.id,
            status: getReference(this.selectedStatus)
        }

        if(this.selectedStatusReason && saveCase.status) {
            saveCase.status.reason = getReference(this.selectedStatusReason) || undefined;
        }

        return saveCase;
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadLookups(): Promise<void> {
        const includes: string[] = [
            'reasons'
        ];
        const fields: string[] = [
            'description',
            'reasons.description',
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadCaseStatuses({ all: true }, includes, undefined, fields);
            this.statuses = response.results;

            const caseStatus = this.case?.status;
            const caseStatusReason = this.case?.status?.reason;
            if(caseStatus && caseStatus.id) {
                this.selectedStatus = this.statuses.find(s => s.id === caseStatus.id) || null;
                this.reasons = this.selectedStatus?.reasons || null;
                if(caseStatusReason && caseStatusReason.id && this.reasons) {
                    this.selectedStatusReason = this.reasons.find(r => r.id === caseStatusReason.id) || null;
                }
            }
            else {
                this.selectedStatus = null;
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async saveCaseStatus(event: Event): Promise<void> {
        const saveCase = this.constructSaveObject();

        try {
            this.state.beginSave();
            const response = await this.service.updateCase(saveCase);
            this.toaster.standard.saved('cm:caseStatus.$', response.isSuccessful);
            if(response.isSuccessful) {
                if(!this.context.case) { this.context.case = {}; }
                this.context.case.status = saveCase.status;
                this.case = this.context.case;

                this.events.publish(new CaseChangeEvent({ id: this.caseId }));
            }
        }
        catch(error) {
            this.toaster.standard.saved('cm:caseStatus.$', false);
            throw error;
        }
        finally {
            this.state.endSave();
        }

    }
}
